import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { C, F, useThemeStore } from '../../design-system/tokens';
import { Button } from '../../design-system/atoms/Button';
import { Card } from '../../design-system/atoms/Card';
import { useAuthStore } from '../../domains/auth/authStore';
import { isBillingLive } from '../../domains/billing/provider';
import {
  createPayment,
  fetchCompanySubscription,
  fetchPlatformBillingContact,
  getAvailablePlans,
  type CompanySubscription,
  type PlatformBillingContact,
  type SubscriptionPlan,
} from '../superadmin/api-subscriptions';
import { useSubscription, type FeatureKey } from './useSubscription';
import { PaymentMethodCard } from './PaymentMethodCard';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_CONTACT: PlatformBillingContact = {
  platform_name: 'Кворум',
  billing_email: 'FieldSnap@ya.ru',
  billing_telegram_url: 'https://t.me/fieldsnap_support',
  billing_whatsapp_url: null,
  billing_portal_url: null,
  billing_contact_note: 'Для подключения и смены тарифа свяжитесь с поддержкой.',
  grace_period_days: 3,
  renewal_notice_days: [7, 3, 1],
};

const FALLBACK_PLANS: SubscriptionPlan[] = [
  {
    id: 'pilot',
    name: 'Пилот',
    description: 'Для знакомства с платформой',
    price_monthly: 0,
    price_annual: 0,
    max_companies: 1,
    max_users_per_company: 10,
    features: [
      'offline_mode', 'push_notifications', 'photo_upload', 'signature_capture',
    ],
    is_active: true,
    currency: 'RUB',
    stripe_product_id: null,
    stripe_price_monthly_id: null,
    stripe_price_annual_id: null,
    is_published: true,
    created_at: '',
    updated_at: '',
  },
  {
    id: 'basic',
    name: 'Базовый',
    description: 'Для малых компаний (3–10 сотрудников)',
    price_monthly: 1990,
    price_annual: 17900,
    max_companies: 1,
    max_users_per_company: 5,
    features: [
      'offline_mode', 'push_notifications', 'photo_upload', 'signature_capture',
      'repeat_orders', 'checklist_builder',
    ],
    is_active: true,
    currency: 'RUB',
    stripe_product_id: null,
    stripe_price_monthly_id: null,
    stripe_price_annual_id: null,
    is_published: true,
    created_at: '',
    updated_at: '',
  },
  {
    id: 'pro',
    name: 'Профессиональный',
    description: 'Для растущих сервисных компаний',
    price_monthly: 3990,
    price_annual: 35900,
    max_companies: 1,
    max_users_per_company: 13,
    features: [
      'offline_mode', 'push_notifications', 'photo_upload', 'signature_capture',
      'repeat_orders', 'checklist_builder', 'analytics', 'multi_dispatcher',
    ],
    is_active: true,
    currency: 'RUB',
    stripe_product_id: null,
    stripe_price_monthly_id: null,
    stripe_price_annual_id: null,
    is_published: true,
    created_at: '',
    updated_at: '',
  },
  {
    id: 'enterprise',
    name: 'Корпоративный',
    description: 'Для крупных организаций',
    price_monthly: 6990,
    price_annual: 62900,
    max_companies: null,
    max_users_per_company: null,
    features: [
      'offline_mode', 'push_notifications', 'photo_upload', 'signature_capture',
      'repeat_orders', 'checklist_builder', 'analytics', 'multi_dispatcher',
      'api_access', 'white_label',
    ],
    is_active: true,
    currency: 'RUB',
    stripe_product_id: null,
    stripe_price_monthly_id: null,
    stripe_price_annual_id: null,
    is_published: true,
    created_at: '',
    updated_at: '',
  },
];

const FEATURE_LABELS: Record<string, string> = {
  // Реальные code-gated функции (FeatureKey type)
  offline_mode: 'Офлайн-режим',
  push_notifications: 'Push-уведомления',
  photo_upload: 'Загрузка фото',
  signature_capture: 'Подпись клиента',
  checklist_builder: 'Конструктор чеклистов',
  analytics: 'Аналитика',
  repeat_orders: 'Повторные заявки',
  multi_dispatcher: 'Мультидиспетчер',
  api_access: 'Доступ к API',
  white_label: 'White-label',
};

const FEATURE_ICONS: Record<string, string> = {
  offline_mode: '📶', push_notifications: '🔔', photo_upload: '📷',
  signature_capture: '✍️', checklist_builder: '✅', analytics: '📊',
  repeat_orders: '🔁', multi_dispatcher: '👥', api_access: '🔌',
  white_label: '🏷️',
};

const FEATURE_NAMES: Record<FeatureKey, string> = {
  offline_mode: 'Офлайн-режим',
  push_notifications: 'Push-уведомления',
  photo_upload: 'Загрузка фото',
  signature_capture: 'Подпись клиента',
  analytics: 'Аналитика',
  checklist_builder: 'Конструктор чеклистов',
  repeat_orders: 'Повторные выезды',
  multi_dispatcher: 'Несколько диспетчеров',
  api_access: 'API-доступ',
  white_label: 'White-label',
};

const STATUS_ACCENT: Record<string, { color: string; soft: string; border: string }> = {
  active:    { color: C.gr,  soft: C.grS,  border: `${C.gr}44`  },
  trial:     { color: C.bl,  soft: C.blS,  border: `${C.bl}44`  },
  grace:     { color: C.am,  soft: C.amS,  border: `${C.am}44`  },
  cancelled: { color: C.re,  soft: C.reS,  border: `${C.re}44`  },
  expired:   { color: C.re,  soft: C.reS,  border: `${C.re}44`  },
  suspended: { color: C.re,  soft: C.reS,  border: `${C.re}44`  },
  none:      { color: C.acc, soft: C.accS, border: `${C.acc}44` },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPlans(plans: SubscriptionPlan[]): SubscriptionPlan[] {
  return plans.length > 0 ? plans : FALLBACK_PLANS;
}

function findPlanByName(plans: SubscriptionPlan[], planName: string | null): SubscriptionPlan | null {
  if (!planName) return null;
  const normalized = planName.trim().toLowerCase();
  return plans.find((p) => p.name.trim().toLowerCase() === normalized) ?? null;
}

function calcSavingsPct(monthly: number, annual: number): number {
  if (monthly <= 0 || annual <= 0) return 0;
  return Math.round(((monthly * 12 - annual) / (monthly * 12)) * 100);
}

function buildRequestHref(
  contact: PlatformBillingContact,
  plan: SubscriptionPlan | null,
  billing: 'monthly' | 'annual',
  userName: string,
  userEmail: string,
  companyName: string,
): string {
  if (contact.billing_portal_url) return contact.billing_portal_url;

  if (!plan) {
    const subj = encodeURIComponent(`${contact.platform_name} — запрос подключения тарифа`);
    const body = encodeURIComponent(
      `Здравствуйте!\n\nКомпания: ${companyName}\nКонтакт: ${userName} (${userEmail})\n\nПрошу помочь с выбором и подключением тарифа.`,
    );
    return `mailto:${contact.billing_email}?subject=${subj}&body=${body}`;
  }

  const billingLabel = billing === 'annual' ? 'Годовая' : 'Месячная';
  const price =
    billing === 'annual'
      ? plan.price_annual > 0
        ? `${plan.price_annual.toLocaleString('ru-RU')} ₽/год`
        : 'FREE'
      : plan.price_monthly > 0
      ? `${plan.price_monthly.toLocaleString('ru-RU')} ₽/мес`
      : 'FREE';

  const subj = encodeURIComponent(
    `${contact.platform_name} — тариф «${plan.name}» (${billingLabel.toLowerCase()})`,
  );
  const body = encodeURIComponent(
    `Здравствуйте!\n\nКомпания: ${companyName}\nКонтакт: ${userName} (${userEmail})\nТариф: ${plan.name}\nПодписка: ${billingLabel} (${price})\n\nПрошу подключить указанный тариф.\n`,
  );
  return `mailto:${contact.billing_email}?subject=${subj}&body=${body}`;
}

type SelfServiceAction =
  | { kind: 'none' }
  | { kind: 'link'; href: string; label: string; helperText?: string }
  | { kind: 'button'; label: string; helperText?: string; disabled?: boolean; busy?: boolean; onClick: () => void };

// ─── FeatureChip ──────────────────────────────────────────────────────────────

function FeatureChip({ feature }: { feature: string }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 8px', borderRadius: 8,
      background: C.cardHi, border: `1px solid ${C.b}`,
      color: C.sub, fontSize: 11, lineHeight: 1.1,
    }}>
      <span style={{ fontSize: 10 }}>{FEATURE_ICONS[feature] ?? '•'}</span>
      <span>{FEATURE_LABELS[feature] ?? feature}</span>
    </div>
  );
}

// ─── PlansToggle ──────────────────────────────────────────────────────────────

function PlansToggle({
  billing,
  onChange,
}: {
  billing: 'monthly' | 'annual';
  onChange: (v: 'monthly' | 'annual') => void;
}) {
  return (
    <div style={{
      display: 'inline-flex', background: C.surf,
      borderRadius: 12, padding: 3, border: `1px solid ${C.b}`,
    }}>
      {(['monthly', 'annual'] as const).map((value) => {
        const active = billing === value;
        return (
          <button
            key={value}
            onClick={() => onChange(value)}
            style={{
              border: 'none', cursor: 'pointer',
              padding: '7px 18px', borderRadius: 9,
              fontSize: 13, fontFamily: F.head, fontWeight: 700,
              background: active ? C.acc : 'transparent',
              color: active ? '#fff' : C.sub,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {value === 'monthly' ? 'Месяц' : 'Год'}
            {value === 'annual' && (
              <span style={{
                fontSize: 10, padding: '1px 5px', borderRadius: 4,
                background: active ? 'rgba(255,255,255,.18)' : `${C.gr}22`,
                color: active ? '#fff' : C.gr,
              }}>−20%</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── PlanCard ─────────────────────────────────────────────────────────────────

interface PlanCardProps {
  plan: SubscriptionPlan;
  billing: 'monthly' | 'annual';
  expanded: boolean;
  isCurrent: boolean;
  onToggle: () => void;
  action: SelfServiceAction;
}

function PlanCard({ plan, billing, expanded, isCurrent, onToggle, action }: PlanCardProps) {
  const isFree = plan.price_monthly <= 0;
  const annualPerMonth = plan.price_annual > 0 ? Math.round(plan.price_annual / 12) : 0;
  const savingsPct = calcSavingsPct(plan.price_monthly, plan.price_annual);

  const borderColor = isCurrent
    ? `${C.gr}55`
    : expanded
    ? `${C.acc}77`
    : C.b;

  const bgColor = isCurrent
    ? `${C.gr}0A`
    : expanded
    ? `${C.acc}0D`
    : C.card;

  const priceMain =
    billing === 'annual' && !isFree
      ? `${plan.price_annual.toLocaleString('ru-RU')} ₽`
      : !isFree
      ? `${plan.price_monthly.toLocaleString('ru-RU')} ₽`
      : 'FREE';

  const priceSub =
    billing === 'annual' && !isFree
      ? '/год'
      : !isFree
      ? '/мес'
      : null;

  return (
    <div style={{
      borderRadius: 16,
      border: `1.5px solid ${borderColor}`,
      background: bgColor,
      overflow: 'hidden',
    }}>
      {/* Header row */}
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          padding: expanded ? '14px 16px 10px' : '14px 16px',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        {/* Left: name + badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 16, fontFamily: F.head, fontWeight: 800,
            color: C.txt, whiteSpace: 'nowrap',
          }}>
            {plan.name}
          </span>
          {isCurrent && (
            <span style={{
              padding: '2px 7px', borderRadius: 999,
              background: C.grS, border: `1px solid ${C.gr}44`,
              color: C.gr, fontSize: 10, fontFamily: F.head, fontWeight: 700,
              letterSpacing: '.4px', textTransform: 'uppercase', flexShrink: 0,
            }}>
              Активный
            </span>
          )}
        </div>

        {/* Right: price + chevron */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: 17, fontFamily: F.head, fontWeight: 800,
              color: isFree ? C.sub : C.txt, lineHeight: 1,
            }}>
              {priceMain}
            </div>
            {priceSub && (
              <div style={{ fontSize: 10, color: C.sub, marginTop: 1 }}>{priceSub}</div>
            )}
          </div>
          <div style={{
            fontSize: 16, color: C.sub, lineHeight: 1,
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform .18s',
          }}>›</div>
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: '0 16px 16px' }}>

          {/* Annual savings chip */}
          {billing === 'annual' && !isFree && savingsPct > 0 && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 8,
              background: C.grS, border: `1px solid ${C.gr}33`,
              marginBottom: 12,
            }}>
              <span style={{
                fontSize: 12, color: C.gr,
                fontFamily: F.head, fontWeight: 700,
              }}>
                {annualPerMonth.toLocaleString('ru-RU')} ₽/мес · экономия {savingsPct}%
              </span>
            </div>
          )}

          {/* Description */}
          {plan.description && (
            <div style={{ fontSize: 12, color: C.sub, marginBottom: 12, lineHeight: 1.55 }}>
              {plan.description}
            </div>
          )}

          {/* Metrics */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{
              flex: 1, padding: '8px 10px', borderRadius: 10,
              background: C.bg, border: `1px solid ${C.b}`,
            }}>
              <div style={{ fontSize: 10, color: C.sub, marginBottom: 2 }}>Пользователей</div>
              <div style={{ fontSize: 20, fontFamily: F.head, fontWeight: 800, color: C.txt }}>
                {plan.max_users_per_company ?? '∞'}
              </div>
            </div>
            <div style={{
              flex: 1, padding: '8px 10px', borderRadius: 10,
              background: C.bg, border: `1px solid ${C.b}`,
            }}>
              <div style={{ fontSize: 10, color: C.sub, marginBottom: 2 }}>Компаний</div>
              <div style={{ fontSize: 20, fontFamily: F.head, fontWeight: 800, color: C.txt }}>
                {plan.max_companies ?? '∞'}
              </div>
            </div>
          </div>

          {/* Features */}
          {plan.features.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {plan.features.map((f) => <FeatureChip key={f} feature={f} />)}
            </div>
          )}

          {/* CTA */}
          {isCurrent && action.kind === 'none' ? (
            <div style={{
              padding: '11px', borderRadius: 12,
              border: `1px solid ${C.gr}44`, background: C.grS,
              color: C.gr, fontSize: 13, fontFamily: F.head, fontWeight: 700,
              textAlign: 'center', letterSpacing: '.3px',
            }}>
              ✓ Ваш текущий тариф
            </div>
          ) : action.kind === 'link' ? (
            <a href={action.href} style={{ textDecoration: 'none', display: 'block' }}>
              <div style={{
                padding: '12px', borderRadius: 12,
                border: `1.5px solid ${C.acc}`, background: C.acc,
                color: '#fff', fontSize: 14, fontFamily: F.head, fontWeight: 800,
                textAlign: 'center', cursor: 'pointer', letterSpacing: '.3px',
              }}>
                {action.label}
              </div>
            </a>
          ) : action.kind === 'button' ? (
            <button
              type="button"
              onClick={action.onClick}
              disabled={action.disabled || action.busy}
              style={{
                width: '100%',
                padding: '12px', borderRadius: 12,
                border: `1.5px solid ${action.disabled ? C.dim : C.acc}`,
                background: action.disabled ? `${C.dim}22` : C.acc,
                color: '#fff', fontSize: 14, fontFamily: F.head, fontWeight: 800,
                textAlign: 'center', cursor: action.disabled ? 'not-allowed' : 'pointer',
                letterSpacing: '.3px', opacity: action.busy ? .7 : 1,
              }}
            >
              {action.busy ? 'Переходим…' : action.label}
            </button>
          ) : null}

          {action.kind !== 'none' && action.helperText && (
            <div style={{ marginTop: 8, fontSize: 11, color: C.sub, lineHeight: 1.45 }}>
              {action.helperText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SubscriptionPlansModal ───────────────────────────────────────────────────

// Ширина, с которой окно показывается по центру (десктоп/планшет), а не как
// нижний лист (мобайл). 768px — общий брейкпойнт планшета в проекте.
function useIsWideViewport(): boolean {
  const [wide, setWide] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true,
  );
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return wide;
}

export function SubscriptionPlansModal({
  open,
  onClose,
  companyId,
  highlightUpgrade = false,
  featureLabel,
}: {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  highlightUpgrade?: boolean;
  featureLabel?: string;
}) {
  // Подписка на тему: модалка перерисуется при переключении, чтобы C.* взяли
  // актуальную палитру.
  useThemeStore((s) => s.theme);
  const isWide = useIsWideViewport();
  const authUser = useAuthStore((state) => state.user);
  const authCompanyId = authUser?.company_id ?? null;
  const activeCompanyId = companyId ?? authCompanyId ?? undefined;

  const { planName, status } = useSubscription();
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [contact, setContact] = useState<PlatformBillingContact>(DEFAULT_CONTACT);
  const [subscription, setSubscription] = useState<CompanySubscription | null>(null);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [actionPlanId, setActionPlanId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const companyName =
    authUser?.memberships?.find((m) => m.company_id === authCompanyId)?.company_name
    ?? authCompanyId
    ?? 'Неизвестная компания';
  const userName = authUser?.name ?? '';
  const userEmail = authUser?.email ?? '';

  useEffect(() => {
    if (!open || !activeCompanyId) return;
    let alive = true;
    setLoading(true);
    setActionError(null);

    Promise.all([
      getAvailablePlans().catch(() => [] as SubscriptionPlan[]),
      fetchCompanySubscription(activeCompanyId).catch(() => null),
      fetchPlatformBillingContact().catch(() => DEFAULT_CONTACT),
    ])
      .then(([planRows, subscriptionRow, billingContact]) => {
        if (!alive) return;
        const safePlans = getPlans(planRows);
        setPlans(safePlans);
        setSubscription(subscriptionRow);
        setContact(billingContact);

        const hasActive = ['active', 'trial', 'grace'].includes(status);
        const resolvedCurrent = hasActive
          ? (
              subscriptionRow?.plan
              ?? (subscriptionRow?.plan_id
                ? safePlans.find((p) => p.id === subscriptionRow.plan_id) ?? null
                : null)
              ?? (planName ? findPlanByName(safePlans, planName) : null)
            ) ?? null
          : null;

        const defaultExpanded =
          resolvedCurrent
          ?? safePlans.find((p) => p.price_monthly > 0)
          ?? safePlans[0]
          ?? null;
        setExpandedPlanId(defaultExpanded?.id ?? null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [activeCompanyId, open, planName, status]);

  const safePlans = useMemo(() => getPlans(plans), [plans]);

  // 1.26.0 — P0-2: idempotency-ключи под пары (planId, billing).
  // Ключ генерируется один раз на первый клик по конкретному плану в
  // конкретном цикле и переиспользуется при повторных кликах в той же
  // mount-сессии компонента. При unmount/возврате на страницу ref
  // обнуляется — это семантически правильно: новый заход → новая попытка.
  const idempotencyKeysRef = useRef<Map<string, string>>(new Map());

  function getOrCreateIdempotencyKey(planId: string, cycle: 'monthly' | 'annual'): string {
    const compositeKey = `${planId}:${cycle}`;
    let key = idempotencyKeysRef.current.get(compositeKey);
    if (!key) {
      key = crypto.randomUUID();
      idempotencyKeysRef.current.set(compositeKey, key);
    }
    return key;
  }

  async function startCheckout(plan: SubscriptionPlan, fallbackHref: string) {
    if (!activeCompanyId) {
      window.location.href = fallbackHref;
      return;
    }

    setActionPlanId(plan.id);
    setActionError(null);
    try {
      const payment = await createPayment({
        company_id:      activeCompanyId,
        plan_id:         plan.id,
        billing_cycle:   billing,
        return_url:      `${window.location.origin}/admin/billing?payment=result`,
        idempotency_key: getOrCreateIdempotencyKey(plan.id, billing),
      });
      if (payment.confirmation_url) {
        // Переходим на страницу оплаты ЮKassa
        window.location.assign(payment.confirmation_url);
        return;
      }
      window.location.href = fallbackHref;
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Не удалось создать платёж ЮKassa');
    } finally {
      setActionPlanId(null);
    }
  }

  async function openBillingPortal(_fallbackHref: string) {
    // ЮKassa: нет Customer Portal.
    // Для обновления карты — инициируем новый платёж с текущим тарифом (save_payment_method=true).
    const currentPlanId = subscription?.plan_id;
    if (!activeCompanyId || !currentPlanId) {
      setActionError('Для обновления карты оформите платёж заново');
      return;
    }

    setActionPlanId(currentPlanId);
    setActionError(null);
    try {
      const payment = await createPayment({
        company_id:      activeCompanyId,
        plan_id:         currentPlanId,
        billing_cycle:   billing,
        return_url:      `${window.location.origin}/admin/billing?payment=card_updated`,
        // 1.26.0 — P0-2: для смены карты — отдельный namespace ключа
        // (`:card-update` суффикс), чтобы он не сталкивался с обычным
        // checkout по тому же плану.
        idempotency_key: getOrCreateIdempotencyKey(currentPlanId + ':card-update', billing),
      });
      if (payment.confirmation_url) {
        window.location.assign(payment.confirmation_url);
        return;
      }
      setActionError('Не удалось создать платёж для обновления карты');
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Ошибка обновления карты');
    } finally {
      setActionPlanId(null);
    }
  }

  // Обработка возврата после оплаты ЮKassa (?payment=result или ?payment=card_updated)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paymentResult = searchParams.get('payment');
    if (paymentResult === 'result' || paymentResult === 'card_updated') {
      // Убираем параметр из URL без перезагрузки
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      // Принудительно сбрасываем данные подписки — перезагрузятся через useEffect выше
      setSubscription(null);
    }
  }, []);

  const hasActiveSubscription = ['active', 'trial', 'grace'].includes(status);
  const currentPlan = hasActiveSubscription
    ? (
        subscription?.plan
        ?? (subscription?.plan_id
          ? safePlans.find((p) => p.id === subscription.plan_id) ?? null
          : null)
        ?? (planName ? findPlanByName(safePlans, planName) : null)
      ) ?? null
    : null;

  function resolvePlanAction(plan: SubscriptionPlan, isCurrent: boolean): SelfServiceAction {
    const fallbackHref = buildRequestHref(contact, plan, billing, userName, userEmail, companyName);
    const isPaidPlan = billing === 'annual' ? plan.price_annual > 0 : plan.price_monthly > 0;

    if (isCurrent) {
      // Кнопка смены карты — только если ЮKassa активна
      if (isBillingLive() && activeCompanyId && isPaidPlan) {
        return {
          kind: 'button',
          label: 'Обновить способ оплаты',
          helperText: 'Для смены карты будет создан новый платёж ЮKassa с сохранением метода.',
          busy: actionPlanId === plan.id,
          onClick: () => void openBillingPortal(fallbackHref),
        };
      }
      // P0-4 followup — в manual-режиме на текущем плане нужно дать
      // user'у выход на продление. Раньше возвращали 'none' и баннер
      // «Продлить» вёл в тупик («Текущий тариф» без кнопок).
      if (isPaidPlan) {
        return {
          kind: 'link',
          href: fallbackHref,
          label: 'Продлить через поддержку',
          helperText: 'Самостоятельная оплата временно отключена. Напишите в поддержку — мы продлим тариф вручную.',
        };
      }
      return { kind: 'none' };
    }

    // Кнопка оплаты — только если ЮKassa активна
    if (isBillingLive() && activeCompanyId && isPaidPlan) {
      return {
        kind: 'button',
        label: 'Перейти к оплате',
        helperText: 'Откроется форма оплаты ЮKassa для самостоятельной активации тарифа.',
        busy: actionPlanId === plan.id,
        onClick: () => void startCheckout(plan, fallbackHref),
      };
    }

    // manual-режим или бесплатный план — всегда ссылка на поддержку
    return {
      kind: 'link',
      href: fallbackHref,
      label: plan.price_monthly <= 0 ? 'Запросить подключение' : 'Связаться с поддержкой',
      helperText: isBillingLive()
        ? 'Если self-service для тарифа ещё не опубликован, используйте ручное подключение через поддержку.'
        : 'Оплата настраивается через поддержку. Нажмите, чтобы отправить запрос.',
    };
  }

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 120,
        background: 'rgba(2,6,12,.7)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: isWide ? 'center' : 'flex-end',
        justifyContent: 'center',
        padding: isWide ? 24 : 0,
        paddingTop: isWide ? 24 : 40,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520,
          maxHeight: isWide ? '90vh' : '92vh', overflowY: 'auto',
          background: C.surf,
          borderRadius: isWide ? 18 : '22px 22px 0 0',
          border: `1px solid ${C.b}`,
          boxShadow: isWide
            ? '0 24px 64px rgba(0,0,0,.45)'
            : '0 -24px 64px rgba(0,0,0,.45)',
          paddingBottom: 32,
        }}
      >
        {/* Drag handle — только в режиме нижнего листа (мобайл) */}
        {!isWide && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 6px' }}>
            <div style={{ width: 44, height: 4, borderRadius: 999, background: C.bBright }} />
          </div>
        )}

        {/* Header */}
        <div style={{
          padding: '4px 20px 16px',
          borderBottom: `1px solid ${C.b}`,
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: 12,
        }}>
          <div>
            <div style={{
              fontSize: 10, color: C.sub,
              textTransform: 'uppercase', letterSpacing: '.7px',
              fontFamily: F.head, fontWeight: 700,
            }}>
              {featureLabel ? 'Доступ по тарифу' : 'Тарифные планы'}
            </div>
            <div style={{
              marginTop: 4, fontSize: 26, lineHeight: 1,
              fontFamily: F.head, fontWeight: 800, color: C.txt,
            }}>
              {featureLabel ? `Откройте ${featureLabel}` : 'Тарифы Кворум'}
            </div>
            {featureLabel && (
              <div style={{ marginTop: 6, fontSize: 12, color: C.sub, lineHeight: 1.45 }}>
                Функция недоступна на текущем плане. Выберите подходящий тариф.
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 10,
              border: `1px solid ${C.b}`, background: C.surf,
              color: C.sub, cursor: 'pointer', flexShrink: 0, fontSize: 14,
            }}
          >✕</button>
        </div>

        {/* Billing toggle */}
        <div style={{ padding: '14px 20px 0', display: 'flex', justifyContent: 'center' }}>
          <PlansToggle billing={billing} onChange={setBilling} />
        </div>

        {actionError && (
          <div style={{ margin: '14px 20px 0', padding: '10px 12px', borderRadius: 10, background: `${C.re}18`, border: `1px solid ${C.re}33`, color: C.re, fontSize: 12, lineHeight: 1.5 }}>
            {actionError}
          </div>
        )}

        {/* Payment method — shown when company has an active paid subscription */}
        {activeCompanyId && subscription && ['active', 'grace'].includes(subscription.status) && (
          <div style={{ padding: '14px 20px 0' }}>
            <div style={{ fontSize: 11, color: C.sub, fontFamily: F.head, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' }}>
              Способ оплаты
            </div>
            <PaymentMethodCard
              companyId={activeCompanyId}
              onUpdateCard={() => {
                const fallback = buildRequestHref(contact, null, billing, userName, userEmail, companyName);
                void openBillingPortal(fallback);
              }}
              updateCardBusy={actionPlanId === (subscription.plan_id ?? 'portal')}
            />
          </div>
        )}

        {/* Plan cards */}
        <div style={{ padding: '14px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: C.sub, fontFamily: F.head }}>
              Загружаем тарифы…
            </div>
          ) : (
            safePlans.map((plan) => {
              // «Текущим» план считается только при совпадении и плана, и цикла
              // оплаты — иначе на вкладке «Ежегодно» месячная подписка ошибочно
              // помечалась текущей и блокировала переход на годовой цикл.
              const activeCycle = subscription?.billing_cycle ?? 'monthly';
              const isCurrent = currentPlan?.id === plan.id && billing === activeCycle;
              const expanded = expandedPlanId === plan.id;
              const action = resolvePlanAction(plan, isCurrent);

              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  billing={billing}
                  expanded={expanded}
                  isCurrent={isCurrent}
                  onToggle={() => setExpandedPlanId((prev) => (prev === plan.id ? null : plan.id))}
                  action={action}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ─── SubscriptionIndicator — compact pill for header ─────────────────────────

export function SubscriptionIndicator({ companyId }: { companyId: string }) {
  const { loading, planName, status, daysLeft, hasFreshData, error } = useSubscription();
  const [open, setOpen] = useState(false);

  if (loading) return null;

  // 1.19.2 — Оффлайн / cетевая ошибка: ничего не показываем. Иначе пилюля
  // «Подключить тариф» появлялась при потере связи и пугала пилотов.
  if (!hasFreshData || error) return null;

  const hasActivePlan = ['active', 'trial', 'grace'].includes(status) && planName != null;
  const isUrgent = ['none', 'expired', 'cancelled', 'suspended'].includes(status);
  const isGrace = status === 'grace';
  const isExpiringSoon = status === 'active' && daysLeft !== null && daysLeft <= 3;

  let pillText: string;
  if (status === 'active') {
    if (isExpiringSoon) {
      pillText = daysLeft === 0 ? '⚠ Истекает сегодня' : `⚠ Осталось ${daysLeft} ${daysLeft === 1 ? 'день' : 'дня'}`;
    } else {
      pillText = planName!;
    }
  } else if (status === 'trial') {
    pillText = daysLeft !== null ? `Пробный · ${daysLeft}д` : 'Пробный период';
  } else if (isGrace) {
    pillText = daysLeft != null ? `Льготный · ${daysLeft}д` : 'Льготный период';
  } else if (status === 'expired' || status === 'cancelled') {
    pillText = 'Тариф истёк';
  } else if (status === 'suspended') {
    pillText = 'Тариф приостановлен';
  } else {
    pillText = 'Подключить тариф';
  }

  const accent = isExpiringSoon
    ? { color: C.am, soft: 'rgba(245,166,35,.13)', border: `${C.am}66` }
    : STATUS_ACCENT[status] ?? STATUS_ACCENT.none;
  const showDot = isUrgent || isGrace || isExpiringSoon;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          padding: '5px 11px 5px 9px',
          borderRadius: 999,
          border: `1px solid ${accent.border}`,
          background: accent.soft,
          color: accent.color,
          fontSize: 12, fontFamily: F.head, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', gap: 5,
          cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '.2px',
        }}
      >
        {showDot && (
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: accent.color, flexShrink: 0, display: 'inline-block',
          }} />
        )}
        {pillText}
      </button>

      <SubscriptionPlansModal
        open={open}
        onClose={() => setOpen(false)}
        companyId={companyId}
        highlightUpgrade={!hasActivePlan || isGrace}
      />
    </>
  );
}

// ─── AdminSubscriptionExpiryBanner — inline warning for admin panel ──────────

/**
 * Показывается в AdminPanel когда до конца подписки ≤ 3 дней.
 * Для expired/cancelled/suspended показывается критичный вариант.
 */
export function AdminSubscriptionExpiryBanner({ companyId }: { companyId: string }) {
  const { loading, status, daysLeft, isExpired } = useSubscription();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (loading || dismissed) return null;

  const expiresSoon = status === 'active' && daysLeft !== null && daysLeft <= 3;
  const isGrace = status === 'grace';

  if (!expiresSoon && !isGrace && !isExpired) return null;

  const isCritical = isExpired;

  let title: string;
  let body: string;
  let cta: string;

  if (isCritical) {
    title = 'Тариф истёк';
    body = 'Создание новых заявок и назначение техников приостановлено. Продлите тариф, чтобы восстановить работу.';
    cta = 'Продлить тариф';
  } else if (isGrace) {
    const d = daysLeft ?? 0;
    title = 'Льготный период';
    body = `Основная подписка завершена. Осталось ${d} ${d === 1 ? 'день' : d < 5 ? 'дня' : 'дней'} льготного периода.`;
    cta = 'Продлить';
  } else {
    const d = daysLeft!;
    title = d === 0 ? 'Тариф истекает сегодня' : `Тариф истекает через ${d} ${d === 1 ? 'день' : 'дня'}`;
    body = 'После окончания тарифа создание новых заявок будет приостановлено. Продлите заранее.';
    cta = 'Продлить';
  }

  return (
    <>
      <div
        style={{
          margin: '0 16px 8px',
          padding: '11px 14px',
          borderRadius: 12,
          background: isCritical ? 'rgba(255,59,59,.09)' : 'rgba(245,166,35,.09)',
          border: `1.5px solid ${isCritical ? C.re : C.am}44`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>
          {isCritical ? '🔒' : '⏳'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              fontFamily: F.head,
              color: isCritical ? C.re : C.am,
              letterSpacing: '.4px',
              marginBottom: 4,
            }}
          >
            {title.toUpperCase()}
          </div>
          <div style={{ fontSize: 12, color: C.txt, lineHeight: 1.5 }}>{body}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
          <button
            onClick={() => setOpen(true)}
            style={{
              padding: '5px 10px',
              borderRadius: 8,
              border: 'none',
              background: isCritical ? C.re : C.am,
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              fontFamily: F.head,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {cta}
          </button>
          {!isCritical && (
            <button
              onClick={() => setDismissed(true)}
              style={{
                padding: '4px 10px',
                borderRadius: 8,
                border: `1px solid ${C.b}`,
                background: 'transparent',
                color: C.sub,
                fontSize: 10,
                cursor: 'pointer',
              }}
            >
              Скрыть
            </button>
          )}
        </div>
      </div>
      <SubscriptionPlansModal
        open={open}
        onClose={() => setOpen(false)}
        companyId={companyId}
        highlightUpgrade
      />
    </>
  );
}

export function SubscriptionNudgeController({ companyId }: { companyId: string }) {
  const { loading, status, hasFreshData, error } = useSubscription();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    // 1.19.2 — Не открываем модал «подключите тариф» если у нас нет
    // подтверждённого ответа сервера. Без этой защиты офлайн-пилот
    // получал nudge при каждом старте сессии — и пугался что подписка
    // якобы кончилась.
    if (!hasFreshData || error) return;
    // 1.19.3 — status='none' (нет подписки в БД) больше не триггерит nudge.
    // Это рабочее состояние для пилотных сред — у компании ещё нет тарифа,
    // но это не повод вешать модал каждые 6 часов. Подсказка о тарифе
    // остаётся доступной через явные точки входа (settings, billing).
    const shouldPrompt =
      status === 'expired' || status === 'cancelled' ||
      status === 'suspended' || status === 'grace';
    if (!shouldPrompt) return;

    const storageKey = `fieldsnap-subscription-nudge:${companyId}:${status}`;
    const lastShownRaw = window.localStorage.getItem(storageKey);
    const lastShownAt = lastShownRaw ? Number(lastShownRaw) : 0;
    if (lastShownAt && Date.now() - lastShownAt < 6 * 60 * 60 * 1000) return;

    const timer = window.setTimeout(() => {
      window.localStorage.setItem(storageKey, String(Date.now()));
      setOpen(true);
    }, 3500);

    return () => window.clearTimeout(timer);
  }, [companyId, loading, status, hasFreshData, error]);

  return (
    <SubscriptionPlansModal
      open={open}
      onClose={() => setOpen(false)}
      companyId={companyId}
      highlightUpgrade
    />
  );
}

// ─── SubscriptionFeatureLock ──────────────────────────────────────────────────

export function SubscriptionFeatureLock({ feature }: { feature: FeatureKey }) {
  const companyId = useAuthStore((state) => state.user?.company_id ?? '');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setOpen(true), 240);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <>
      <Card style={{
        padding: '22px 18px', borderRadius: 14,
        textAlign: 'center', display: 'flex',
        flexDirection: 'column', gap: 10,
      }}>
        <div style={{ fontSize: 28 }}>🔒</div>
        <div style={{ fontSize: 16, fontFamily: F.head, fontWeight: 800, color: C.txt }}>
          {FEATURE_NAMES[feature] ?? feature}
        </div>
        <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.55, maxWidth: 340, alignSelf: 'center' }}>
          Этот раздел доступен на платном тарифе. Выберите подходящий план для активации.
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button ch="Посмотреть тарифы" onClick={() => setOpen(true)} />
        </div>
      </Card>

      <SubscriptionPlansModal
        open={open}
        onClose={() => setOpen(false)}
        companyId={companyId}
        highlightUpgrade
        featureLabel={FEATURE_NAMES[feature] ?? feature}
      />
    </>
  );
}

// ─── SubscriptionPriceLabel ───────────────────────────────────────────────────

export function SubscriptionPriceLabel({ value }: { value: number }) {
  if (value <= 0) return <>FREE</>;
  return <>{value.toLocaleString('ru-RU')} ₽</>;
}
